import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { DRUG_INFO } from 'src/assets/static/constants';
import { LoadingScreen } from 'src/components/loading-screen';
import { Page } from 'src/components/Page';
import { useStores } from 'src/models';
import { PATH_AUTH, PATH_PAGE } from 'src/routes/paths';
import { settingsApi } from 'src/services';

type AuthGuardProps = {
  children: React.ReactNode;
};

function AuthGuard({ children }: AuthGuardProps) {
  const { authStore } = useStores();
  const { isAuthenticated, isInitialized } = authStore;

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [medicineNotificationChecked, setMedicineNotificationChecked] = useState<boolean>(false);

  // 금연일 설정 > FTND > 문진 > 복용약 문진 및 설정 > 서비스 이용 가능
  const requireSurveyPaths = useMemo(
    () => [
      PATH_PAGE.home,
      PATH_PAGE.drJin.root,
      PATH_PAGE.mission.root,
      PATH_PAGE.craving.root,
      PATH_PAGE.calendar.root,
    ],
    [],
  );

  const excludedPaths = useMemo(
    () => [
      PATH_PAGE.quitSmokingDateSettings,
      PATH_PAGE.ftnd.root,
      PATH_PAGE.medicalQuestion.root,
      PATH_PAGE.myPage.root,
      PATH_PAGE.drugQuestion.root,
      PATH_PAGE.medicineSettings.root,
    ],
    [],
  );

  // 복용약 알림 설정 조회
  const getUserNotificationMedicine = async () => {
    const result = await settingsApi.getUserNotificationMedicine();
    if (result?.resultCode === 'S') {
      const userNotiMedicine = result.data;
      if (
        userNotiMedicine?.userMedicine &&
        userNotiMedicine?.userMedicineNotificationList.length === 0
      ) {
        // 복용중인 약이 없는 경우 기본 값 set
        if (userNotiMedicine?.userMedicine.drugInfoSid === DRUG_INFO.NONE.drugInfoSid) {
          const setNotiResult = await settingsApi.setDefaultUserNotificationMedicine(
            userNotiMedicine.userMedicine,
          );
          return setNotiResult?.resultCode !== 'S';
        } else {
          return true;
        }
      } else {
        setMedicineNotificationChecked(true);
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    const checkHomeAccess = async () => {
      const userProfile = authStore.user;
      if (!userProfile) return;

      // 예외 경로 or 문진 필수 경로가 아닌 경우 return;
      if (
        excludedPaths.some((path) => pathname.startsWith(path)) ||
        !requireSurveyPaths.some((path) => pathname.startsWith(path))
      ) {
        return;
      }

      // 문진 완료 여부
      const hasIncompleteMedQuestions = userProfile.medicalQuestionCompleteYn?.some(
        (item) => item.medicalQuestionCompleteYn === 'N',
      );

      // 복용약 문진 완료 여부
      const hasIncompleteDrugQuestions = userProfile.drugQuestionCompleteYn?.some(
        (item) => item.drugQuestionCompleteYn === 'N',
      );

      let redirectPath = null;
      if (!userProfile.quitDt) {
        redirectPath = PATH_PAGE.quitSmokingDateSettings;
      } else if (userProfile.ftndCompleteYn === 'N') {
        redirectPath = PATH_PAGE.ftnd.root;
      } else if (hasIncompleteMedQuestions) {
        redirectPath = PATH_PAGE.medicalQuestion.root;
      } else if (hasIncompleteDrugQuestions) {
        redirectPath = PATH_PAGE.drugQuestion.root;
      } else if (!medicineNotificationChecked) {
        const hasIncompleteMedicineNotification = await getUserNotificationMedicine();
        if (hasIncompleteMedicineNotification) {
          redirectPath = PATH_PAGE.drugQuestion.root;
        }
      }

      if (redirectPath && pathname !== redirectPath) {
        navigate(redirectPath);
      }
    };

    checkHomeAccess();
  }, [
    authStore.user,
    navigate,
    pathname,
    isInitialized,
    isAuthenticated,
    medicineNotificationChecked,
    excludedPaths,
    requireSurveyPaths,
  ]);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={PATH_AUTH.login} replace state={{ from: pathname }} />;
  }

  return (
    <Page id="auth" style={{ padding: 0, height: '100%' }}>
      {children}
    </Page>
  );
}

export default observer(AuthGuard);
